
import { Component, Prop, Watch } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import { namespace } from 'vuex-class';
import VFormBuilder from '@/components/shared/form/VFormBuilder.vue';
import { InputType } from '@/enums/InputType';
import { MerchantChannel } from '@/enums/SmoothrPay';
import Countries from '@/mixins/Countries';
import UserApiService from '@/api/http/UserApiService';
import { User } from '@/interfaces/models/User';
import { CreateMerchantConfigurationRequest, MerchantAccount } from '@/interfaces/models/SmoothrPay';
import { industryCodeOptions, isWebAddress } from '@/util/smoothrPay';

const auth = namespace('auth');
const userApi: UserApiService = new UserApiService();

/**
 * Creates a merchant configuration in `draft`.
 *
 * Everything Adyen needs up front: the legal entity, exactly one business line,
 * the sales channels, one balance account description and the Smoothr user who
 * owns it. The service validates the same rules — a line selling online must
 * carry a web address — so the dialog refuses to submit without it rather than
 * bouncing off a 422.
 */
@Component({ components: { VFormBuilder } })
export default class MerchantConfigurationDialog extends mixins(Countries) {
  @Prop({ type: Boolean, default: false }) public visible!: boolean;
  @Prop({ type: Boolean, default: false }) public loading!: boolean;
  @Prop({ type: Array, default: () => [] }) public merchantAccounts!: MerchantAccount[];

  @auth.State('user') public authUser!: Partial<User> | null;

  public $refs!: {
    form: InstanceType<typeof VFormBuilder> & { form: any; validate: () => Promise<boolean> };
  };

  public users: User[] = [];
  public error: string = '';

  get initialValues() {
    return {
      country: 'de',
      channels: [MerchantChannel.ECOM],
      balanceAccount: 'Main',
      industryCode: industryCodeOptions()[0].value,
      owner: this.authUser && this.authUser.id ? this.authUser.id : '',
    };
  }

  get channelItems() {
    return [
      { text: this.$t('smoothrPay.channels.ecom'), value: MerchantChannel.ECOM },
      { text: this.$t('smoothrPay.channels.pos'), value: MerchantChannel.POS },
    ];
  }

  get accountItems() {
    return this.merchantAccounts.map((account: MerchantAccount) => ({
      text: account.isDefault ? `${account.id} (${this.$t('smoothrPay.fields.defaultAccount')})` : account.id,
      value: account.id,
    }));
  }

  /** Searchable owner list. Only an admin may onboard on someone else's behalf. */
  get ownerItems() {
    return this.users.map((user: User) => ({
      text: user.email ? `${user.name || user.email} (${user.email})` : user.name,
      value: user._id,
    }));
  }

  get items() {
    return [
      {
        name: 'merchantAccount',
        type: InputType.Select,
        label: 'smoothrPay.fields.merchantAccount',
        items: this.accountItems,
        visible: this.merchantAccounts.length > 1,
      },
      { name: 'name', type: InputType.Text, label: 'smoothrPay.fields.legalName', rules: 'required' },
      { name: 'description', type: InputType.Text, label: 'smoothrPay.fields.description', rules: 'required' },
      {
        name: 'country',
        type: InputType.Select,
        label: 'smoothrPay.fields.country',
        items: this.countries,
        rules: 'required',
      },
      {
        name: 'channels',
        type: InputType.Select,
        label: 'smoothrPay.fields.channels',
        items: this.channelItems,
        multiple: true,
        rules: 'required',
      },
      // One business line, as the service's create schema expects. Adyen wants a
      // six-digit NAICS code, so it is picked rather than typed.
      {
        name: 'industryCode',
        type: InputType.Select,
        label: 'smoothrPay.fields.industryCode',
        hint: 'smoothrPay.hints.industryCode',
        items: industryCodeOptions(),
        rules: 'required',
      },
      {
        name: 'webAddress',
        type: InputType.Text,
        label: 'smoothrPay.fields.webAddress',
        hint: 'smoothrPay.hints.webAddress',
      },
      {
        name: 'balanceAccount',
        type: InputType.Text,
        label: 'smoothrPay.fields.balanceAccount',
        rules: 'required',
      },
      // Searchable, and admin-only: the policy rejects any other owner for
      // everyone else, so they get their own account without a picker.
      {
        name: 'owner',
        type: InputType.Autocomplete,
        label: 'smoothrPay.fields.owner',
        hint: 'smoothrPay.hints.ownerAdmin',
        items: this.ownerItems,
        rules: 'required',
        visible: this.$isAdmin(),
      },
    ];
  }

  public mounted() {
    this.fetchUsers();
  }

  @Watch('visible')
  public onVisibleChange(visible: boolean) {
    if (visible) {
      this.error = '';
      if (!this.users.length) {
        this.fetchUsers();
      }
    }
  }

  /**
   * The endpoint paginates to ten per page when given a page, and returns every
   * user without one — a picker needs all of them.
   */
  public async fetchUsers() {
    if (!this.$isAdmin()) {
      return;
    }
    try {
      const { data } = await userApi.index();
      this.users = Array.isArray(data) ? data : (data as any).data || [];
    } catch (error) {
      this.users = [];
    }
  }

  public async save() {
    this.error = '';
    const valid = await this.$refs.form.validate();
    if (!valid) {
      return;
    }

    const form: any = this.$refs.form.form;
    const channels: MerchantChannel[] = form.channels || [];

    // Adyen rejects the capability check without a web address on an online
    // channel, and the service enforces the format either way.
    const sellsOnline = channels.indexOf(MerchantChannel.ECOM) > -1;
    if (sellsOnline && !form.webAddress) {
      this.error = this.$t('smoothrPay.create.webAddressRequired') as string;
      return;
    }
    if (form.webAddress && !isWebAddress(form.webAddress)) {
      this.error = this.$t('smoothrPay.create.webAddressInvalid') as string;
      return;
    }

    const owner = this.resolveOwner(form.owner);
    if (!owner) {
      this.error = this.$t('smoothrPay.create.ownerRequired') as string;
      return;
    }

    const payload: CreateMerchantConfigurationRequest = {
      legalEntity: { name: form.name, description: form.description, country: form.country },
      businessLines: [
        {
          industryCode: form.industryCode,
          salesChannels: channels,
          // Absent rather than '' — the service validates the URI when present.
          ...(form.webAddress ? { webAddress: form.webAddress } : {}),
        },
      ],
      channels,
      balanceAccounts: [{ description: form.balanceAccount }],
      owner,
    };
    if (form.merchantAccount) {
      payload.merchantAccount = form.merchantAccount;
    }
    this.$emit('save', payload);
  }

  /** The picked user, or the session user when there was no picker. */
  private resolveOwner(id: string): { id: string; name: string; email: string } | null {
    if (this.$isAdmin() && id) {
      const user = this.users.find((candidate: User) => candidate._id === id);
      if (user) {
        return { id: user._id, name: user.name, email: user.email };
      }
    }
    const self = this.authUser;
    if (self && self.id) {
      return { id: self.id, name: self.name || '', email: self.email || '' };
    }
    return null;
  }
}
